import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { getTitoloI18n } from '../Utils/titoloUtils'
import * as FiIcons from 'react-icons/fi'
import ArticoloRow from './ArticoloRow'
import { Api } from '../Services'
import {
  DrawerOverlay,
  DrawerContainer,
  DrawerHeader,
  DrawerHeaderTop,
  DrawerTitle,
  CloseButton,
  SearchContainer,
  SearchInput,
  SearchIcon,
  DrawerBody,
  DrawerFooter,
  FooterText,
  Badge,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './ArticoliDrawer.styles'

const PAGE_SIZE = 50

export default function ArticoliDrawer ({
  isOpen,
  onClose,
  settore,
  items,
  itemsGiaAggiunti,
  onAddItem,
  onInfo,
  tipo,
  valuta,
  azienda
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [serverItems, setServerItems] = useState([])
  const [serverTotal, setServerTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)

  const isServerMode = tipo === 'articoli' && !!azienda

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
      setDebouncedSearch('')
      setServerItems([])
      setServerTotal(0)
      setPage(1)
    }
  }, [isOpen])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
      setPage(1)
    }, 300)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [searchTerm])

  const fetchPage = useCallback(async (targetPage, replace) => {
    if (!isServerMode || !isOpen) return
    setLoading(true)
    try {
      const params = { azienda, page: targetPage, limit: PAGE_SIZE, sort: 'titolo_it:asc' }
      if (settore) params.settore = settore
      if (debouncedSearch) params.search = debouncedSearch
      const res = await Api.get('/articoli', params)
      if (res.ok) {
        setServerItems(prev => replace ? res.data.items : [...prev, ...res.data.items])
        setServerTotal(res.data.total || 0)
      }
    } finally {
      setLoading(false)
    }
  }, [isServerMode, isOpen, azienda, settore, debouncedSearch])

  useEffect(() => {
    if (isServerMode && isOpen) fetchPage(1, true)
  }, [isServerMode, isOpen, settore, debouncedSearch, fetchPage])

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  const filteredClientItems = useMemo(() => {
    if (isServerMode) return null
    const list = items || []
    if (!searchTerm.trim()) return list
    const term = searchTerm.toLowerCase()
    return list.filter(item => {
      if (tipo === 'articoli') {
        return (
          getTitoloI18n(item).toLowerCase().includes(term) ||
          item.referenza?.toLowerCase().includes(term) ||
          item.modello?.toLowerCase().includes(term) ||
          item.marca?.toLowerCase().includes(term) ||
          item.fornitore?.fornitore?.toLowerCase().includes(term) ||
          item.produttore?.produttore?.toLowerCase().includes(term)
        )
      }
      return (
        getTitoloI18n(item).toLowerCase().includes(term) ||
        item.descrizione?.toLowerCase().includes(term)
      )
    })
  }, [items, searchTerm, tipo, isServerMode])

  const visibleItems = isServerMode ? serverItems : filteredClientItems
  const isItemAdded = (id) => itemsGiaAggiunti.includes(id)
  const countAggiunti = itemsGiaAggiunti.length
  const hasMore = isServerMode && serverItems.length < serverTotal

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleLoadMore = () => {
    const next = page + 1
    setPage(next)
    fetchPage(next, false)
  }

  return (
    <>
      <DrawerOverlay isOpen={isOpen} onClick={handleOverlayClick} />
      <DrawerContainer isOpen={isOpen}>
        <DrawerHeader>
          <DrawerHeaderTop>
            <DrawerTitle>
              {tipo === 'articoli' ? 'Aggiungi Materiale' : 'Aggiungi Servizi'}
            </DrawerTitle>
            <CloseButton onClick={onClose} title='Chiudi (ESC)'>
              <FiIcons.FiX size={24} />
            </CloseButton>
          </DrawerHeaderTop>

          <SearchContainer>
            <SearchIcon>
              <FiIcons.FiSearch size={18} />
            </SearchIcon>
            <SearchInput
              type='text'
              placeholder={`Cerca ${tipo === 'articoli' ? 'per titolo, referenza, marca...' : 'per titolo o descrizione...'}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </SearchContainer>
        </DrawerHeader>

        <DrawerBody>
          {visibleItems && visibleItems.length > 0
            ? (
              <>
                {visibleItems.map(item => (
                  <ArticoloRow
                    key={item._id}
                    item={item}
                    tipo={tipo}
                    isAdded={isItemAdded(item._id)}
                    onAdd={onAddItem}
                    onInfo={onInfo}
                    valuta={valuta}
                  />
                ))}
                {hasMore && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                    <button
                      type='button'
                      disabled={loading}
                      onClick={handleLoadMore}
                      style={{ padding: '0.5rem 1.5rem', cursor: loading ? 'wait' : 'pointer' }}
                    >
                      {loading ? 'Caricamento...' : `Carica altri (${serverTotal - serverItems.length} rimanenti)`}
                    </button>
                  </div>
                )}
              </>
              )
            : (
              <EmptyState>
                <EmptyStateIcon>
                  <FiIcons.FiInbox />
                </EmptyStateIcon>
                <EmptyStateText>
                  {loading
                    ? 'Caricamento...'
                    : (searchTerm
                        ? `Nessun ${tipo === 'articoli' ? 'articolo' : 'servizio'} trovato`
                        : `Nessun ${tipo === 'articoli' ? 'articolo' : 'servizio'} disponibile`)}
                </EmptyStateText>
              </EmptyState>
              )}
        </DrawerBody>

        <DrawerFooter>
          <FooterText>
            {tipo === 'articoli' ? 'Articoli' : 'Servizi'} nel carrello:
            <Badge>{countAggiunti}</Badge>
            {isServerMode && serverTotal > 0 && (
              <span style={{ marginLeft: '1rem', fontSize: '0.75rem', opacity: 0.7 }}>
                {serverItems.length} / {serverTotal}
              </span>
            )}
          </FooterText>
          <CloseButton onClick={onClose}>
            <FiIcons.FiX size={20} />
            Chiudi
          </CloseButton>
        </DrawerFooter>
      </DrawerContainer>
    </>
  )
}
